import {
  createWorkflowTemplate,
  createWorkflowTemplateVersion,
} from '@commenda/api';
import {
  Alert,
  Button,
  FieldWrapper,
  Input,
  Label,
  Textarea,
  useToggle,
} from '@commenda/kakashi';
import mixpanel, {
  MIXPANEL_EVENTS,
  MIXPANEL_SURFACE_AREA,
} from '@commenda/mixpanel';
import { TCreateWorkflowTemplateParams } from '@commenda/types';
import { cn } from '@commenda/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { useNotification } from '../../../hooks/useNotification';

const createWorkflowTemplateSchema = z.object({
  name: z.string().min(1),
  description: z.string().optional(),
  externalName: z.string().min(1),
  key: z.string().optional(),
});

type TCreateWorkflowTemplateFormFields = z.infer<
  typeof createWorkflowTemplateSchema
>;

const FIRST_VERSION = '1.0.0';

export const CreateWorkflowTemplateForm = ({
  onCancel,
  onSuccess,
  formContentClassName,
  formFooterClassName,
}: {
  onCancel: () => void;
  /** Receives the DRAFT 1.0.0 created alongside the template, so the caller can open the editor. */
  onSuccess: (workflowTemplateVersionId: string) => void;
  formContentClassName?: string;
  formFooterClassName?: string;
}) => {
  const isSubmitting = useToggle(false);
  const { setNotification } = useNotification();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<TCreateWorkflowTemplateFormFields>({
    resolver: zodResolver(createWorkflowTemplateSchema),
  });

  const onSubmit = async (data: TCreateWorkflowTemplateFormFields) => {
    isSubmitting.on();
    try {
      const res = await createWorkflowTemplate(
        data as TCreateWorkflowTemplateParams
      );
      if (!res.success || !res.data) {
        setNotification({
          title: 'Error',
          message: 'Failed to create workflow.',
          show: true,
          valid: false,
        });
        isSubmitting.off();
        return;
      }

      const workflowTemplateId = res.data.workflowTemplate.id;
      const versionRes = await createWorkflowTemplateVersion({
        workflowTemplateId,
        version: FIRST_VERSION,
      });
      if (!versionRes.success || !versionRes.data) {
        // The template exists and is listed; only the first version is missing. Say which half
        // failed, otherwise the author sees a workflow they cannot open and no reason why.
        setNotification({
          title: 'Version not created',
          message: `Workflow "${data.name}" was created, but its first version could not be. Open it from the table and add version ${FIRST_VERSION}.`,
          show: true,
          valid: false,
        });
        isSubmitting.off();
        return;
      }

      setNotification({
        title: 'Success',
        message: `Workflow created with draft version ${FIRST_VERSION}. Opening the editor.`,
        show: true,
        valid: true,
      });
      onSuccess(versionRes.data.workflowTemplateVersion.id);
      mixpanel.track(MIXPANEL_EVENTS.WORKFLOW_ADDED, {
        surface_area: MIXPANEL_SURFACE_AREA.WORKFLOW_AND_FORM_BUILDER,
        workflow_name: data.name,
      });
    } catch (error) {
      console.error('Error creating workflow template:', error);
    } finally {
      isSubmitting.off();
    }
  };

  return (
    <div>
      <div className={cn('flex flex-col gap-4', formContentClassName)}>
        <FieldWrapper>
          <Label>
            Name <span className="text-text-error">*</span>
          </Label>
          <Input {...register('name')} error={errors.name?.message} />
        </FieldWrapper>

        <FieldWrapper>
          <Label>
            External name <span className="text-text-error">*</span>
          </Label>
          <Alert
            variant="info"
            description="The external name serves as the customer-facing title of any service request created using this template."
          />
          <Input
            {...register('externalName')}
            error={errors.externalName?.message}
          />
        </FieldWrapper>
        <FieldWrapper>
          <Label>Key</Label>
          <Input {...register('key')} error={errors.key?.message} />
        </FieldWrapper>
        <FieldWrapper>
          <Label>Description</Label>
          <Textarea
            {...register('description')}
            error={errors.description?.message}
          />
        </FieldWrapper>
      </div>
      <div className={cn('flex gap-2 justify-end', formFooterClassName)}>
        <Button type="button" variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button disabled={isSubmitting.value} onClick={handleSubmit(onSubmit)}>
          {isSubmitting.value ? 'Creating...' : 'Create'}
        </Button>
      </div>
    </div>
  );
};
